import { useEffect, useState, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getFilteredGamesStart, toggleFavoriteStart } from '../../../store/redux-slices/casinoMenu'
import { isMobileOrTablet } from '../../../utils/helpers'
import { getItem } from 'utils/storageUtils'
import { toast } from 'components/Toast'
import { deviceType } from 'components/DashboardSections/Constant'
import { useIsInitialMount } from 'hooks/useIsInitialMount'
import { GAME_FILTER_SUPPORT_TYPE } from '../../../constants/uiConfig'
import useFavoriteGames from '../../../hooks/useFavoriteGames'
import { expandProvidersWithStudios } from '../../../utils/provider'

const useSearchGames = () => {
  const isInitialMount = useIsInitialMount()

  const dispatch = useDispatch()
  const [selectedFilter, setSelectedFilter] = useState({ provider: [], studio: [], rating: [], search: '' })
  const {
    gameSearch,
    filteredLoading,
    providers = [],
    studiosByOrder
  } = useSelector((state) => state.casinoMenu)

  // Build studioId -> name map (fallback safe for any value type)
  const studioNameById = useMemo(() => {
    const map = {}
    providers.forEach(provider => {
      if (provider?.TenantGameStudios) {
        provider.TenantGameStudios.forEach(({ GameStudio, id }) => {
          if (GameStudio && id != null && GameStudio.name) {
            map[id] = GameStudio.name
          }
        })
      }
    })
    return map
  }, [providers])

  const providersWithStudios = useMemo(() => expandProvidersWithStudios(providers), [providers?.length])

  const general = useSelector((state) => state?.uiConfig?.general)
  const loggedIn = JSON.parse(getItem('loggedIn'))
  const totalCount = gameSearch?.totalCount
  const { languageData, selectedLanguageCode } = useSelector((state) => state?.language)

  const [selectedSearchGameFilter, setSelectedSearchGameFilter] = useState({ search: '' })
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [pageNo, setPageNo] = useState(1)
  const limit = 20
  const [isLoadAllActive, setIsLoadAllActive] = useState(false)
  const mobileOrTablet = isMobileOrTablet()
  const bottomRef = useRef(null)
  const { gameFilterSupportType = 'PROVIDER' } = general || {}
  const { favoriteGamesList } = useFavoriteGames()

  const hasMore = pageNo * limit < totalCount

  const loadMore = () => {
    if (gameSearch?.rows?.length < totalCount) {
      setPageNo((prev) => prev + 1)
    }
  }

  const loadAll = () => {
    setIsLoadAllActive(true)
    if (gameSearch?.rows?.length < totalCount) {
      setPageNo((prev) => prev + 1)
    }
  }

  // Debounce search field only
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(selectedSearchGameFilter.search)
    }, 500)
    return () => clearTimeout(handler)
  }, [selectedSearchGameFilter.search])

  const getLatestGamesData = () => {
    const providerNamesSelected = selectedFilter.provider || []

    if (gameFilterSupportType === GAME_FILTER_SUPPORT_TYPE.PROVIDER) {
      // Provider-only flow: send provider names in API params and ignore studios
      dispatch(
        getFilteredGamesStart({
          search: debouncedSearch || '',
          provider: providerNamesSelected,
          studio: [],
          gameInputSearch: true,
          pageNo,
          limit,
          fetchMore: pageNo > 1,
          deviceType,
          source: providerNamesSelected.length === 0 ? 'search-popup' : ''
        })
      )
      return
    }

    // Default flow: expand selected providers to studio names and merge with directly selected studios
    const providersSource = providers || []
    const studioNamesFromProviders = new Set()
    providerNamesSelected.forEach(name => {
      const providerObj = providersSource.find(provider => provider?.name === name)
      const studios = Array.isArray(providerObj?.TenantGameStudios) ? providerObj.TenantGameStudios : []
      studios.forEach(({ GameStudio }) => { if (GameStudio?.name) studioNamesFromProviders.add(GameStudio.name) })
    })

    const studioNamesFromIds = (selectedFilter.studio || [])
      .map(id => studioNameById[id])
      .filter(Boolean)

    const studioNames = Array.from(new Set([
      ...studioNamesFromIds,
      ...Array.from(studioNamesFromProviders)
    ]))

    dispatch(
      getFilteredGamesStart({
        search: debouncedSearch || '',
        studio: studioNames,
        gameInputSearch: true,
        pageNo,
        limit,
        fetchMore: pageNo > 1,
        deviceType,
        source: providerNamesSelected.length === 0 && selectedFilter.studio?.length === 0 ? 'search-popup' : ''
      })
    )
  }

  useEffect(() => {
    if (isInitialMount) return
    getLatestGamesData()
  }, [pageNo])

  useEffect(() => {
    if (pageNo !== 1) {
      setPageNo(1)
      setIsLoadAllActive(false)
      return
    }
    getLatestGamesData()
    setIsLoadAllActive(false)
  }, [selectedFilter.provider, selectedFilter.studio, debouncedSearch])

  // Infinite scrolling logic
  useEffect(() => {
    if (!isLoadAllActive || !hasMore) return

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore()
        }
      },
      { threshold: 0.1 }
    )

    if (bottomRef.current) {
      observer.observe(bottomRef.current)
    }

    return () => {
      if (bottomRef.current) {
        observer.unobserve(bottomRef.current)
      }
    }
  }, [isLoadAllActive, hasMore])

  const toggleFavorite = ({ isFavorite, id }) => {
    if (loggedIn) {
      dispatch(toggleFavoriteStart({
        isFavorite,
        id,
        fromGlobalSearchModal: true
      }))
    } else {
      toast(languageData?.loginFirst, 'error')
    }
  }

  return {
    languageData,
    loading: filteredLoading,
    gameSearch,
    selectedSearchGameFilter,
    setSelectedSearchGameFilter,
    mobileOrTablet,
    loadMore,
    loadAll,
    hasMore,
    isLoadAllActive,
    bottomRef,
    toggleFavorite,
    selectedLanguageCode,
    selectedFilter,
    setSelectedFilter,
    general,
    gameFilterSupportType,
    providers,
    studios: studiosByOrder,
    favoriteGamesList,
    providersWithStudios
  }
}

export default useSearchGames
